import { useEffect, useState } from "react";
import { updateMentorRequest, fetchMentorPreparation, fetchMenteePreparation } from "../lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import MeetingNotesPanel from "./MeetingNotesPanel";
import { MentorApprovedActions } from "./IntegrationActions";
import { ArrowRight, CheckCircle2, XCircle, Clock, GraduationCap, MessageSquare, Building2, Sparkles, HelpCircle, Ear, Trophy, Target, RefreshCw, Eye, ListTodo, Quote, Heart } from "lucide-react";
import { toast } from "sonner";

export const STATUS_META = {
  pending: { label: "Bekliyor", color: "amber", icon: Clock },
  approved: { label: "Onaylandı", color: "emerald", icon: CheckCircle2 },
  rejected: { label: "Reddedildi", color: "rose", icon: XCircle },
  completed: { label: "Tamamlandı", color: "sky", icon: GraduationCap },
};

const STATUS_BADGE = {
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rose: "bg-rose-50 text-rose-800 border-rose-200",
  sky: "bg-sky-50 text-sky-800 border-sky-200",
};

const CADENCE_LABEL = {
  haftalik: "Haftalık",
  "iki-haftalik": "İki haftada bir",
  aylik: "Aylık",
};

export function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[0.7rem] font-medium border ${STATUS_BADGE[meta.color]}`}
      data-testid={`req-status-${status}`}
    >
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

/**
 * Shared row component used by both HR Command Center (all requests view)
 * and Mentor Inbox (incoming requests for a specific employee acting as mentor).
 *
 * Props:
 *  - item: mentor request object
 *  - onUpdate: callback(updated)
 *  - perspective: 'hr' | 'mentor' (mentor view emphasizes mentee + uses warmer copy)
 */
export default function MentorRequestRow({ item, onUpdate, perspective = "hr", currentUserId }) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [responseMsg, setResponseMsg] = useState("");
  const [prep, setPrep] = useState(null);
  const [prepLoading, setPrepLoading] = useState(false);

  const isMentorPerspective = perspective === "mentor";
  const isMenteeSentPerspective = perspective === "mentee_sent";

  // Load AI preparation pack lazily when row is expanded.
  // Mentor view loads mentor prep, mentee_sent view loads mentee prep.
  useEffect(() => {
    if (!expanded || prep) return;
    if (!isMentorPerspective && !isMenteeSentPerspective) return;
    // Mentor prep is only meaningful for pending; mentee prep is useful any time
    if (isMentorPerspective && item.status !== "pending") return;
    let cancelled = false;
    setPrepLoading(true);
    const fetcher = isMentorPerspective ? fetchMentorPreparation : fetchMenteePreparation;
    fetcher(item.id)
      .then((d) => {
        if (!cancelled) setPrep(d);
      })
      .catch(() => {
        if (!cancelled) setPrep(null);
      })
      .finally(() => {
        if (!cancelled) setPrepLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [expanded, isMentorPerspective, isMenteeSentPerspective, item.status, item.id, prep]);

  const reloadPrep = async () => {
    setPrepLoading(true);
    setPrep(null);
    try {
      const fetcher = isMentorPerspective ? fetchMentorPreparation : fetchMenteePreparation;
      const data = await fetcher(item.id, true);
      setPrep(data);
    } catch {
      // ignore
    } finally {
      setPrepLoading(false);
    }
  };

  const handleAction = async (newStatus) => {
    setBusy(true);
    try {
      const updated = await updateMentorRequest(item.id, {
        status: newStatus,
        response_message: responseMsg,
      });
      onUpdate(updated);
      toast.success(
        newStatus === "approved"
          ? "Talep onaylandı"
          : newStatus === "rejected"
            ? "Talep reddedildi"
            : "Durum güncellendi",
        { description: `${item.mentee_name} → ${item.mentor_name}` },
      );
      setResponseMsg("");
      setExpanded(false);
    } catch (e) {
      toast.error("Durum güncellenemedi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-surface p-5" data-testid={`mentor-request-row-${item.id}`}>
      <div className="flex items-start gap-5 flex-wrap">
        {/* Mentee */}
        <div className="flex items-center gap-3 min-w-[200px]">
          <Avatar className={`w-11 h-11 ring-2 ${isMenteeSentPerspective ? "ring-violet-500/20" : "ring-sky-500/20"}`}>
            <AvatarImage src={item.mentee_avatar} />
            <AvatarFallback>{item.mentee_name?.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{item.mentee_name}</div>
            <div className="text-xs text-slate-500 truncate">{item.mentee_title}</div>
            <div className="text-[0.65rem] text-slate-400 uppercase tracking-wider mt-0.5">
              {isMentorPerspective ? "Sana ulaşan kişi" : isMenteeSentPerspective ? "Sen" : "Talep eden"}
            </div>
          </div>
        </div>

        <ArrowRight className="w-4 h-4 text-slate-300 mt-3" />

        {/* Mentor */}
        <div className="flex items-center gap-3 min-w-[200px]">
          <Avatar className={`w-11 h-11 ring-2 ${isMenteeSentPerspective ? "ring-sky-500/30" : "ring-amber-500/20"}`}>
            <AvatarImage src={item.mentor_avatar} />
            <AvatarFallback>{item.mentor_name?.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{item.mentor_name}</div>
            <div className="text-xs text-slate-500 truncate">{item.mentor_title}</div>
            <div className="text-[0.65rem] text-slate-400 uppercase tracking-wider mt-0.5">
              {isMentorPerspective ? "Sen (mentor)" : isMenteeSentPerspective ? "İstediğin mentor" : "Mentor"}
            </div>
          </div>
        </div>

        <div className="flex-1" />

        <div className="text-right space-y-1.5">
          <StatusPill status={item.status} />
          <div className="text-[0.65rem] text-slate-500 font-mono">{item.id}</div>
          <div className="text-[0.65rem] text-slate-400">
            {new Date(item.submitted_at).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" })}
          </div>
        </div>
      </div>

      {/* Meta row */}
      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-5 flex-wrap text-xs">
        <span className="inline-flex items-center gap-1.5 text-slate-600">
          <Clock className="w-3.5 h-3.5" /> {CADENCE_LABEL[item.proposed_cadence] || item.proposed_cadence}
        </span>
        <span className="inline-flex items-center gap-1.5 text-slate-600">
          <Building2 className="w-3.5 h-3.5" /> {item.mentee_department}
        </span>
        {item.focus_areas?.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Sparkles className="w-3.5 h-3.5 text-sky-600" />
            {item.focus_areas.slice(0, 3).map((f) => (
              <span key={f} className="text-[0.7rem] px-2 py-0.5 rounded-full bg-sky-50 text-sky-800 border border-sky-200">
                {f}
              </span>
            ))}
            {item.focus_areas.length > 3 && (
              <span className="text-[0.65rem] text-slate-400">+{item.focus_areas.length - 3}</span>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="ml-auto text-[0.7rem] text-sky-700 hover:underline"
          data-testid={`req-toggle-${item.id}`}
        >
          {expanded
            ? "Kapat"
            : isMenteeSentPerspective
              ? "Hazırlık paketini gör"
              : item.status === "pending"
                ? "Yanıtla / Aksiyon"
                : "Detay"}
        </button>
      </div>

      {/* Expanded message + actions */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
          {item.message && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
                <MessageSquare className="w-3 h-3" /> {isMentorPerspective ? `${item.mentee_name}'in notu` : "Mentee notu"}
              </div>
              <p className="text-sm text-slate-800 leading-relaxed">{item.message}</p>
            </div>
          )}
          {item.response_message && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
              <div className="text-[0.65rem] uppercase tracking-wider text-emerald-700 mb-1">
                {isMentorPerspective ? "Senin yanıtın" : "Mentor yanıtı"}
              </div>
              <p className="text-sm text-emerald-900 leading-relaxed">{item.response_message}</p>
            </div>
          )}

          {/* AI Preparation Pack — mentor view, pending requests only */}
          {isMentorPerspective && item.status === "pending" && (
            <div
              data-testid={`prep-pack-${item.id}`}
              className="rounded-xl border-2 border-sky-200 bg-gradient-to-br from-sky-50/80 via-white to-white p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-sky-500/20 border border-sky-400/50 flex items-center justify-center">
                    <Sparkles className="w-3.5 h-3.5 text-sky-700" />
                  </div>
                  <div>
                    <div className="text-[0.65rem] uppercase tracking-wider text-sky-700 font-semibold">AI Hazırlık Paketi</div>
                    <div className="text-xs text-slate-500">{`İlk görüşme öncesi ${item.mentee_name.split(" ")[0]}'i tanı`}</div>
                  </div>
                </div>
                {prep && !prepLoading && (
                  <button
                    type="button"
                    onClick={reloadPrep}
                    className="text-[0.65rem] text-sky-700 hover:underline inline-flex items-center gap-1"
                    data-testid={`prep-refresh-${item.id}`}
                  >
                    <RefreshCw className="w-3 h-3" /> Yenile
                  </button>
                )}
              </div>

              {prepLoading && (
                <div className="space-y-2.5" data-testid={`prep-loading-${item.id}`}>
                  <div className="h-3 bg-slate-200/60 rounded animate-pulse w-full" />
                  <div className="h-3 bg-slate-200/60 rounded animate-pulse w-5/6" />
                  <div className="h-3 bg-slate-200/60 rounded animate-pulse w-3/4" />
                  <div className="text-[0.65rem] text-slate-500 italic mt-2">
                    {`Plena AI ${item.mentee_name.split(" ")[0]}'in profilini analiz ediyor…`}
                  </div>
                </div>
              )}

              {prep && !prepLoading && (
                <div className="space-y-4">
                  {prep.mentee_snapshot && (
                    <div>
                      <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
                        <Target className="w-3 h-3 text-sky-600" /> Mentee Anlık Profil
                      </div>
                      <p className="text-sm text-slate-800 leading-relaxed">{prep.mentee_snapshot}</p>
                    </div>
                  )}

                  {prep.mentee_top_gaps?.length > 0 && (
                    <div>
                      <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-2">
                        Öncelikli Gelişim Açıkları
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {prep.mentee_top_gaps.map((g) => (
                          <div
                            key={g.skill_name}
                            className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md border border-amber-200 bg-amber-50 text-xs"
                          >
                            <span className="text-amber-900 font-medium">{g.skill_name}</span>
                            <span className="font-mono text-[0.65rem] text-amber-700">{g.current}→{g.target}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {prep.opening_questions?.length > 0 && (
                    <div>
                      <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                        <HelpCircle className="w-3 h-3 text-sky-600" /> Açılış Soruları
                      </div>
                      <ol className="space-y-1.5">
                        {prep.opening_questions.map((q, i) => (
                          <li key={i} className="text-sm text-slate-800 flex gap-2">
                            <span className="font-mono text-xs text-sky-700 font-semibold w-5 flex-shrink-0">{i + 1}.</span>
                            <span className="leading-snug">{q}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {prep.listening_for?.length > 0 && (
                    <div>
                      <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                        <Ear className="w-3 h-3 text-sky-600" /> Dinlerken Dikkat Et
                      </div>
                      <ul className="space-y-1">
                        {prep.listening_for.map((l, i) => (
                          <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-sky-400 mt-2 flex-shrink-0" />
                            <span>{l}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {prep.quick_wins?.length > 0 && (
                    <div>
                      <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                        <Trophy className="w-3 h-3 text-emerald-600" /> İlk 30 Günde Hızlı Kazanımlar
                      </div>
                      <ul className="space-y-1">
                        {prep.quick_wins.map((w, i) => (
                          <li key={i} className="text-sm text-emerald-900 flex items-start gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                            <span>{w}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {prep.first_meeting_outcome && (
                    <div className="rounded-lg bg-sky-100/60 border border-sky-300 px-3 py-2.5">
                      <div className="text-[0.65rem] uppercase tracking-wider text-sky-800 mb-1">
                        İlk Görüşme Çıktısı
                      </div>
                      <p className="text-sm text-sky-900 leading-relaxed">→ {prep.first_meeting_outcome}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* AI Mentee Preparation Pack — mentee_sent perspective, any status */}
          {isMenteeSentPerspective && (
            <div
              data-testid={`mentee-prep-pack-${item.id}`}
              className="rounded-xl border-2 border-violet-200 bg-gradient-to-br from-violet-50/80 via-white to-white p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-violet-500/20 border border-violet-400/50 flex items-center justify-center">
                    <Sparkles className="w-3.5 h-3.5 text-violet-700" />
                  </div>
                  <div>
                    <div className="text-[0.65rem] uppercase tracking-wider text-violet-700 font-semibold">Görüşmene Hazırlık</div>
                    <div className="text-xs text-slate-500">
                      {item.status === "pending"
                        ? `${item.mentor_name.split(" ")[0]} onayını bekliyor — sen bu arada hazırlan`
                        : item.status === "approved"
                          ? `${item.mentor_name.split(" ")[0]} kabul etti — görüşmen için hazır ol`
                          : item.status === "rejected"
                            ? "Mentor bu sefer müsait değildi — başka mentor önerilerine bak"
                            : `${item.mentor_name.split(" ")[0]} ile yolculuğun tamamlandı`}
                    </div>
                  </div>
                </div>
                {prep && !prepLoading && (
                  <button
                    type="button"
                    onClick={reloadPrep}
                    className="text-[0.65rem] text-violet-700 hover:underline inline-flex items-center gap-1"
                    data-testid={`mentee-prep-refresh-${item.id}`}
                  >
                    <RefreshCw className="w-3 h-3" /> Yenile
                  </button>
                )}
              </div>

              {prepLoading && (
                <div className="space-y-2.5" data-testid={`mentee-prep-loading-${item.id}`}>
                  <div className="h-3 bg-slate-200/60 rounded animate-pulse w-full" />
                  <div className="h-3 bg-slate-200/60 rounded animate-pulse w-5/6" />
                  <div className="h-3 bg-slate-200/60 rounded animate-pulse w-3/4" />
                  <div className="text-[0.65rem] text-slate-500 italic mt-2">
                    {`Plena AI ${item.mentor_name.split(" ")[0]} ile görüşmen için hazırlık yapıyor…`}
                  </div>
                </div>
              )}

              {prep && !prepLoading && (
                <div className="space-y-4">
                  {prep.mentor_snapshot && (
                    <div>
                      <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
                        <Target className="w-3 h-3 text-violet-600" /> Mentorun Hakkında
                      </div>
                      <p className="text-sm text-slate-800 leading-relaxed">{prep.mentor_snapshot}</p>
                    </div>
                  )}

                  {prep.things_to_ask?.length > 0 && (
                    <div>
                      <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                        <HelpCircle className="w-3 h-3 text-violet-600" /> Sorabileceğin Sorular
                      </div>
                      <ol className="space-y-1.5">
                        {prep.things_to_ask.map((q, i) => (
                          <li key={i} className="text-sm text-slate-800 flex gap-2">
                            <span className="font-mono text-xs text-violet-700 font-semibold w-5 flex-shrink-0">{i + 1}.</span>
                            <span className="leading-snug">{q}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {prep.things_to_expect?.length > 0 && (
                    <div>
                      <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                        <Eye className="w-3 h-3 text-violet-600" /> Ne Bekleyebilirsin
                      </div>
                      <ul className="space-y-1">
                        {prep.things_to_expect.map((e, i) => (
                          <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-violet-400 mt-2 flex-shrink-0" />
                            <span>{e}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {prep.prep_actions?.length > 0 && (
                    <div>
                      <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                        <ListTodo className="w-3 h-3 text-violet-600" /> Görüşmeden Önce Yap
                      </div>
                      <ul className="space-y-1">
                        {prep.prep_actions.map((a, i) => (
                          <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5 text-violet-500 mt-0.5 flex-shrink-0" />
                            <span>{a}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {prep.first_5_min_opener && (
                    <div className="rounded-lg bg-violet-100/60 border border-violet-300 px-3 py-3">
                      <div className="text-[0.65rem] uppercase tracking-wider text-violet-800 mb-1.5 flex items-center gap-1.5">
                        <Quote className="w-3 h-3" /> İlk 5 Dakika İçin Açılış Önerisi
                      </div>
                      <p className="text-sm text-violet-900 leading-relaxed italic">{prep.first_5_min_opener}</p>
                    </div>
                  )}

                  {prep.confidence_note && (
                    <div className="flex items-start gap-2 text-xs text-slate-600 italic pt-1">
                      <Heart className="w-3.5 h-3.5 text-rose-500 flex-shrink-0 mt-0.5" />
                      <span>{prep.confidence_note}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {item.status === "pending" && !isMenteeSentPerspective && (
            <>
              <Textarea
                data-testid={`req-response-${item.id}`}
                value={responseMsg}
                onChange={(e) => setResponseMsg(e.target.value)}
                placeholder={isMentorPerspective ? "Mentee'ye not bırak (opsiyonel)…" : "Mentor yanıtı (opsiyonel)…"}
                rows={2}
                className="bg-white border-slate-200 resize-none text-sm"
              />
              <div className="flex gap-2">
                <Button
                  data-testid={`req-approve-${item.id}`}
                  onClick={() => handleAction("approved")}
                  disabled={busy}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" /> {isMentorPerspective ? "Kabul et" : "Onayla"}
                </Button>
                <Button
                  data-testid={`req-reject-${item.id}`}
                  onClick={() => handleAction("rejected")}
                  disabled={busy}
                  variant="outline"
                  className="flex-1 border-rose-300 text-rose-700 hover:bg-rose-50"
                >
                  <XCircle className="w-4 h-4 mr-2" /> {isMentorPerspective ? "Nazikçe reddet" : "Reddet"}
                </Button>
              </div>
            </>
          )}
          {item.status === "approved" && (
            <div className="space-y-2">
              <Button
                data-testid={`req-complete-${item.id}`}
                onClick={() => handleAction("completed")}
                disabled={busy}
                variant="outline"
                className="border-sky-300 text-sky-800 hover:bg-sky-50"
              >
                <GraduationCap className="w-4 h-4 mr-2" /> Tamamlandı olarak işaretle
              </Button>
              <MentorApprovedActions
                mentor={item.mentor_name}
                mentee={item.mentee_name}
                cadence={item.proposed_cadence}
              />
            </div>
          )}

          {/* Meeting notes — show on approved or completed status when current user is mentor or mentee */}
          {(item.status === "approved" || item.status === "completed") &&
            currentUserId && (currentUserId === item.mentor_id || currentUserId === item.mentee_id) && (
              <MeetingNotesPanel
                requestId={item.id}
                currentUserId={currentUserId}
                mentorId={item.mentor_id}
                menteeId={item.mentee_id}
              />
            )}
        </div>
      )}
    </div>
  );
}
